/**
 * Tearing a panel off into its own window.
 *
 * Only the desktop shell can do this — a browser has no such thing — so every
 * function here answers honestly in both places rather than throwing in one of
 * them. `canDetach()` is what the UI asks before offering the control at all;
 * a detach button in a browser tab that does nothing is worse than no button.
 *
 * ## The window is the same app at a different route
 *
 * Tauri opens `index.html#/pane/<kind>`, which `App.tsx` renders chromeless —
 * one panel, no rail, no switcher. So a pane inherits the API origin, the
 * session, the theme and the formatters that the main window already has, and
 * there is no second application to keep in step.
 *
 * ## It is the same device
 *
 * CME counts devices and "the ability to access" is the billable event. Two
 * windows of one application on one machine are one device, and nothing here
 * registers a second identity — the Rust side reuses the process's own, and
 * the pane window carries the same boot script. See `panes.rs`.
 */

interface PaneBridge {
  invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T>;
}

/**
 * Tauri's invoke, when there is one.
 *
 * Read through `window` rather than imported, so the web build does not pull
 * in a desktop-only dependency it can never use.
 */
function bridge(): PaneBridge | null {
  const core = (globalThis as { __TAURI__?: { core?: PaneBridge } }).__TAURI__?.core;
  return core ?? null;
}

/** True in the desktop shell, false in a browser tab. */
export function canDetach(): boolean {
  return bridge() !== null;
}

/**
 * Which panels this build will open in a window.
 *
 * Deliberately shorter than the full panel list — Notes in a window of its own
 * is a text editor with no reason to exist. Asked of the shell rather than
 * duplicated here, so the two cannot drift; `panes.test.ts` checks that the
 * Rust list and the panel registry still agree.
 */
export async function detachableKinds(): Promise<readonly string[]> {
  const b = bridge();
  if (!b) return [];
  return b.invoke<string[]>('detachable_panes').catch(() => []);
}

/**
 * Open a panel in its own window, or focus the one already open.
 *
 * `symbol` is passed at open time for a symbol-scoped panel rather than read
 * from the main window later. A pane that silently followed whatever the main
 * window last charted would change what it is about without saying so, which
 * on a depth ladder is a different market with no visible change.
 */
export async function openPane(paneId: string, symbol?: string | null): Promise<boolean> {
  const b = bridge();
  if (!b) return false;
  try {
    // The PANE id, not the kind. With several charts on screen, "open the
    // chart in a window" is not a statement about which one — and the shell
    // derives both the window label and the panel to draw from this string.
    await b.invoke('open_pane', { paneId, symbol: symbol ?? null });
    return true;
  } catch {
    // An unknown kind, or a window that would not build. The layout is left
    // alone by the caller, which is better than a workspace claiming a panel
    // is detached into a window that never opened.
    return false;
  }
}

export async function closePane(paneId: string): Promise<void> {
  const b = bridge();
  if (!b) return;
  await b.invoke('close_pane', { paneId }).catch(() => undefined);
}

/**
 * Which panes are open right now, read from the windows themselves.
 *
 * Not from the stored layout: a trader can close a pane with the system button,
 * and a workspace that kept claiming it was detached would leave that panel in
 * neither place — not in the grid, not in a window.
 */
export async function openPaneKinds(): Promise<readonly string[]> {
  const b = bridge();
  if (!b) return [];
  return b.invoke<string[]>('open_panes').catch(() => []);
}

/**
 * The pane this window is, if it is one.
 *
 * Parsed from the hash rather than from a router, because it has to be known
 * before the app decides what to render at all.
 */
export function paneFromLocation(
  hash: string,
): { paneId: string; kind: string; symbol: string | null } | null {
  // `:` is legal in a URL fragment and is what separates an instance from its
  // kind, so it has to be in the character class — without it `#/pane/chart:2`
  // would not match at all and the window would render the whole app.
  const m = /^#\/pane\/([\w:-]+)(?:\?(.*))?$/.exec(hash);
  if (!m) return null;
  const params = new URLSearchParams(m[2] ?? '');
  const paneId = m[1]!;
  return { paneId, kind: paneId.split(':')[0] ?? '', symbol: params.get('symbol') };
}
